import { useMemo } from 'react';

import { clsx } from 'clsx';

import { Button, colors, Divider, IconButton, Select, Skeleton } from '@treezor/design-system';

import { ssoStatusOptions } from '@/features/dashboard-users/store/support-user.model';
import { GeneralInformation } from '@/features/dashboard-users/components/detail/GeneralInformation';

import { roleSlugValues, TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { RoleOption, RoleSelectedOption } from '@/features/dashboard-users/components/RoleOption';
import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';
import useManageDashboardUser from '@/features/dashboard-users/hooks/useManageDashboardUser';
import { TAdvancedDropdownItem } from '@/ui-kit/dropdown/AdvancedDropdown';
import { AdvancedDropdownMenu } from '@/ui-kit/dropdown/AdvancedDropdownMenu';
import { DashboardUser2FAStatus } from '@/features/dashboard-users/components/DashboardUser2FAStatus';

interface IManageDashboardUserModalProps {
  isModalOpen: boolean;
  dashboardUserId: string;
  onClose: () => void;
}
export function ManageDashboardUserModal({ isModalOpen, dashboardUserId, onClose }: IManageDashboardUserModalProps) {
  const {
    t,
    dashboardUser,
    organisationName,
    isSSO,
    isDashboardUserLoading,
    isBlocked,
    lastLogin,
    creationDate,
    accessExpirationDate,
    isOwnAccount,
    role,
    advancedRole,
    isAdvancedRolesLoading,
    dashboardUserRole,
    ssoStatusInfo,
    advancesRolesConfig,
    hasSamlKey,
    onAdvancedRoleChange,
    onClickUpdateSamlKey,
    onReset2Fa,
    toggleUpdatePasswordrModal,
    toggleRemoveUserModal,
    toggleSuspendUserModal,
  } = useManageDashboardUser(dashboardUserId, onClose);

  const moreActionMenuItem: TAdvancedDropdownItem[] = useMemo(
    () =>
      [
        {
          subItems: [
            {
              label: t('pages.administration.supportsUsers.changePassword.action'),
              size: 's',
              onClick: toggleUpdatePasswordrModal,
            },
          ],
        },
        {
          subItems: [
            {
              label: t('pages.administration.supportsUsers.details.moreActions.suspend'),
              size: 's',
              onClick: toggleSuspendUserModal,
              color: colors.red[500],
            },
            {
              label: t('pages.administration.supportsUsers.details.moreActions.remove'),
              size: 's',
              onClick: toggleRemoveUserModal,
              color: colors.red[500],
            },
          ],
        },
      ] as const,
    [t, toggleRemoveUserModal, toggleSuspendUserModal, toggleUpdatePasswordrModal],
  );

  return (
    <div
      className={clsx(
        'fixed right-0 top-0 w-[25rem] h-full overflow-x-hidden overflow-y-auto',
        'bg-primary shadow-100 rounded-100 flex flex-col z-[201] transition-transform',
        'py-7',
        isModalOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <div className="flex items-center justify-between px-7 pb-5">
        <span className="typo-h6-bold text-secondary break-all">{dashboardUser.username}</span>
        <IconButton onClick={onClose} aria-label="close" icon="close" />
      </div>
      <Divider thickness="100" direction="horizontal" className="w-full" />
      <div className="flex flex-col gap-5 px-7 pt-6">
        <GeneralInformation
          dashboardUser={dashboardUser}
          isBlocked={isBlocked}
          lastLogin={lastLogin}
          creationDate={creationDate}
          accessExpirationDate={accessExpirationDate}
          shouldShowExpirationDate={true}
        />
        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-4">
          <p className="typo-subtitle-subtitle2 text-tertiary uppercase">
            {t('pages.administration.supportsUsers.details.account.authorization')}
          </p>
          <div className="grid grid-cols-[auto_1fr] gap-y-3 gap-x-9 items-center ">
            <span className="typo-body-regular-paragraph text-secondary">
              {t('pages.administration.supportsUsers.details.account.organisation')}
            </span>
            <span className="typo-body-regular-paragraph text-secondary">{organisationName}</span>
            {role && (
              <>
                <span className="typo-body-regular-paragraph text-secondary">
                  {t('pages.administration.supportsUsers.details.account.role')}
                </span>
                {isAdvancedRolesLoading || isDashboardUserLoading ? (
                  <Skeleton width="100%" height={40} />
                ) : (
                  <Select
                    size="s"
                    options={
                      advancedRole.slug !== roleSlugValues.ADMINISTRATOR &&
                      dashboardUserRole?.slug !== roleSlugValues.ADMINISTRATOR
                        ? advancesRolesConfig.filter((role) => role.slug !== roleSlugValues.ADMINISTRATOR)
                        : advancesRolesConfig
                    }
                    getOptionLabel={RoleOption}
                    getOptionDisplay={RoleSelectedOption}
                    getOptionValue={(role) => role.slug}
                    defaultValue={dashboardUserRole?.slug}
                    onChange={(roleSlug: TAdvancedRoleSlug) => onAdvancedRoleChange(roleSlug, dashboardUser)}
                  ></Select>
                )}
              </>
            )}
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-4">
          <p className="typo-subtitle-subtitle2 text-tertiary uppercase">
            {t('pages.administration.supportsUsers.details.account.authentication')}
          </p>
          <div className="flex items-center justify-between">
            <div className="grid grid-cols-[auto_1fr] gap-y-3 gap-x-9 items-center ">
              <span className="typo-body-regular-paragraph text-secondary">
                {t('pages.administration.supportsUsers.details.account.sso.title')}
              </span>
              <div className="flex gap-2">
                <span className={clsx('typo-body-regular-paragraph', ssoStatusInfo.color)}>
                  {t(`pages.administration.supportsUsers.details.account.sso.status.${ssoStatusInfo.status}`)}
                </span>
                {ssoStatusInfo.tooltip && (
                  <InfoTooltip
                    tooltipInfo={<span className="typo-body-medium-caption">{ssoStatusInfo.tooltip}</span>}
                    tooltipPlacement="top"
                    infoClassName="min-w-[260px]"
                    iconClassName={
                      ssoStatusInfo.status == ssoStatusOptions.SAMLKeyMissing
                        ? '!text-icon-error !hover:text-icon-error'
                        : ''
                    }
                  />
                )}
              </div>
            </div>

            <Button size="s" variant="secondary" onClick={() => onClickUpdateSamlKey()}>
              {hasSamlKey
                ? t('pages.administration.supportsUsers.details.account.sso.editBtn')
                : t('pages.administration.supportsUsers.details.account.sso.setBtn')}
            </Button>
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" className="w-full" />

        <div className="flex flex-col gap-4">
          <p className="typo-subtitle-subtitle2 text-tertiary uppercase">
            {t('pages.administration.supportsUsers.details.account.security')}
          </p>
          <div className="flex items-center justify-between">
            <div className="grid grid-cols-[auto_1fr] gap-y-3 gap-x-9 items-center ">
              <span className="typo-body-regular-paragraph text-secondary">
                {t('pages.administration.supportsUsers.details.account.2FA')}
              </span>
              <DashboardUser2FAStatus
                dashboardUser={dashboardUser}
                isFirstRow={false}
                statusClassname="!typo-body-regular-paragraph"
              />
            </div>
            {dashboardUser.secureKey && !isSSO && (
              <Button size="s" variant="secondary" onClick={onReset2Fa}>
                {t('pages.cores.reset')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-auto px-7 pt-7">
        {isOwnAccount && isSSO && (
          <Button
            size="s"
            variant="secondary"
            isLoading={isDashboardUserLoading}
            onClick={toggleUpdatePasswordrModal}
            className="w-full"
          >
            {t('pages.administration.supportsUsers.changePassword.action')}
          </Button>
        )}

        {!isOwnAccount && (
          <AdvancedDropdownMenu
            size="s"
            closeOnSelect
            isLoading={isDashboardUserLoading}
            items={moreActionMenuItem}
            className="[&>button:first-child]:w-full"
          >
            {t('pages.administration.supportsUsers.details.moreActions.label')}
          </AdvancedDropdownMenu>
        )}
      </div>
    </div>
  );
}
